import React, { useState, useEffect, useRef } from 'react';

const roles = [
    'Systems Architect',
    'AI / ML Engineer',
    'Scalability Specialist',
];

function Hero() {
    const canvasRef = useRef(null);
    const [typewriterText, setTypewriterText] = useState('');
    const [roleIndex, setRoleIndex] = useState(0);
    const [charIndex, setCharIndex] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);
    const animFrameRef = useRef(null);

    // --- Particle Canvas ---
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        let particles = [];
        let width, height;

        const resize = () => {
            width = window.innerWidth;
            height = window.innerHeight;
            canvas.width = width;
            canvas.height = height;
        };
        resize();
        window.addEventListener('resize', resize);

        // Create particles as "nodes"
        const nodeCount = Math.min(80, Math.floor((width * height) / 12000));
        for (let i = 0; i < nodeCount; i++) {
            particles.push({
                x: Math.random() * width,
                y: Math.random() * height,
                vx: (Math.random() - 0.5) * 0.5,
                vy: (Math.random() - 0.5) * 0.5,
                r: Math.random() * 2 + 1,
                opacity: Math.random() * 0.5 + 0.2,
            });
        }

        const draw = () => {
            ctx.clearRect(0, 0, width, height);

            // Draw edges between nearby particles (like a neural network)
            for (let i = 0; i < particles.length; i++) {
                for (let j = i + 1; j < particles.length; j++) {
                    const dx = particles[i].x - particles[j].x;
                    const dy = particles[i].y - particles[j].y;
                    const dist = Math.sqrt(dx * dx + dy * dy);
                    const maxDist = 160;

                    if (dist < maxDist) {
                        const alpha = (1 - dist / maxDist) * 0.28;
                        ctx.beginPath();
                        ctx.moveTo(particles[i].x, particles[i].y);
                        ctx.lineTo(particles[j].x, particles[j].y);
                        ctx.strokeStyle = `rgba(0, 240, 255, ${alpha})`;
                        ctx.lineWidth = 0.5;
                        ctx.stroke();
                    }
                }
            }

            // Draw particles
            for (const p of particles) {
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(0, 240, 255, ${p.opacity})`;
                ctx.fill();

                // Glow ring
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r + 3, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(124, 58, 237, ${p.opacity * 0.3})`;
                ctx.fill();
            }

            // Update particle positions
            for (const p of particles) {
                p.x += p.vx;
                p.y += p.vy;

                if (p.x < 0) p.x = width;
                if (p.x > width) p.x = 0;
                if (p.y < 0) p.y = height;
                if (p.y > height) p.y = 0;
            }

            animFrameRef.current = requestAnimationFrame(draw);
        };

        draw();

        return () => {
            window.removeEventListener('resize', resize);
            if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
        };
    }, []);

    // --- Typing Animation ---
    useEffect(() => {
        const currentRole = roles[roleIndex];
        let timeout;

        if (!isDeleting && charIndex <= currentRole.length) {
            timeout = setTimeout(() => {
                setTypewriterText(currentRole.substring(0, charIndex));
                setCharIndex(charIndex + 1);
            }, 80);
        } else if (!isDeleting && charIndex > currentRole.length) {
            // Pause at end
            timeout = setTimeout(() => {
                setIsDeleting(true);
            }, 2000);
        } else if (isDeleting && charIndex > 0) {
            timeout = setTimeout(() => {
                setTypewriterText(currentRole.substring(0, charIndex - 1));
                setCharIndex(charIndex - 1);
            }, 40);
        } else if (isDeleting && charIndex === 0) {
            setIsDeleting(false);
            setRoleIndex((roleIndex + 1) % roles.length);
        }

        return () => clearTimeout(timeout);
    }, [charIndex, isDeleting, roleIndex]);

    return (
        <section className="hero" id="hero">
            <canvas ref={canvasRef} className="hero__canvas" aria-hidden="true" />

            {/* Floating hex decorations */}
            <svg
                className="hero__hex hero__hex--1"
                viewBox="0 0 100 100"
                aria-hidden="true"
            >
                <polygon
                    points="50,5 90,28 90,72 50,95 10,72 10,28"
                    fill="none"
                    stroke="rgba(0,240,255,0.45)"
                    strokeWidth="1"
                />
            </svg>
            <svg
                className="hero__hex hero__hex--2"
                viewBox="0 0 100 100"
                aria-hidden="true"
            >
                <polygon
                    points="50,5 90,28 90,72 50,95 10,72 10,28"
                    fill="none"
                    stroke="rgba(124,58,237,0.45)"
                    strokeWidth="1"
                />
            </svg>
            <svg
                className="hero__hex hero__hex--3"
                viewBox="0 0 100 100"
                aria-hidden="true"
            >
                <polygon
                    points="50,5 90,28 90,72 50,95 10,72 10,28"
                    fill="none"
                    stroke="rgba(244,114,182,0.45)"
                    strokeWidth="1"
                />
            </svg>

            <div className="hero__content">
                <div className="hero__portrait">
                    <img src="/portrait.jpg" alt="Portrait of Aleksandr Sorokin" />
                </div>

                <h1 className="hero__name">
                    Aleksandr{' '}
                    <span className="hero__name-accent">Sorokin</span>
                </h1>

                <div className="hero__typewriter">
                    <span>{typewriterText}</span>
                    <span className="cursor" />
                </div>

                <p className="hero__description">
                    B.Sc. Software Development student (graduating 2027) with 4+
                    years of production engineering experience. I design scalable
                    distributed systems and ship ML to production — from pipelines
                    serving 4,000+ external systems to CNN-based medical imaging —
                    now re-architecting a shift platform into five Kubernetes
                    microservices for my finals.
                </p>

                <p className="hero__availability">
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                    >
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                    </svg>
                    Copenhagen, Denmark · Seeking systems / AI-engineering internship 2027 · Open to on-site, hybrid &amp; remote
                </p>

                <div className="hero__cta-group">
                    <a href="#projects" className="btn btn--primary">
                        View Projects
                    </a>
                    <a href="/CV-2026-08.pdf" download="Aleksandr-Sorokin-CV.pdf" className="btn btn--cv">
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Download CV
                    </a>
                    <a href="#contact" className="btn">
                        Get in Touch
                    </a>
                </div>
            </div>

            {/* Diagonal divider */}
            <div className="hero__divider" aria-hidden="true">
                <svg viewBox="0 0 1440 80" preserveAspectRatio="none">
                    <polygon points="0,80 1440,0 1440,80 0,80" />
                </svg>
            </div>

            {/* Scroll indicator */}
            <div className="hero__scroll-indicator">
                <span>Scroll</span>
                <div className="hero__scroll-line" />
            </div>
        </section>
    );
}

export default Hero;